import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type Customer360, type OpportunityDetail, type QuoteRow } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox, ago, label } from "../ui";
import { BcNotice } from "./Accounts";
import { CHECKLIST, Check, LOST_REASONS, ROLES, STAGES, STAGE_NAME, XSection, daysUntil, roleOf, today } from "../crm";

/* ---------------- Opportunities list ---------------- */

const usd = (n: number | null | undefined) => (n === null || n === undefined ? "" : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const usd0 = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });
const usDate = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const ACT = (p: string) => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d={p} fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>;
const ICON = {
  plus: ACT("M8 3v10M3 8h10"), edit: ACT("M3 13l1-3 7-7 2 2-7 7-3 1z"), next: ACT("M3 8h9M9 4l4 4-4 4"), won: ACT("M3 8.5l3 3 7-7"), lost: ACT("M4 4l8 8M12 4l-8 8"),
  mail: ACT("M2 3.5h12v9H2zM2 4l6 5 6-5"), meet: ACT("M2 3h12v11H2zM2 6.5h12M5 1.5v3M11 1.5v3"), log: ACT("M3 4h10M3 8h10M3 12h6"), board: ACT("M2 3h3v10H2zM6.5 3h3v7h-3zM11 3h3v4h-3z"), list: ACT("M3 4h10M3 8h10M3 12h10"), excel: ACT("M2.5 2.5h11v11h-11zM2.5 6.5h11M6.5 2.5v11"),
};

export function PipelinePage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? "mine";
  const shape = params.get("as") ?? "list";
  const { data, error } = useLoad(() => api.opportunities(), []);
  const all = data ?? [];
  const isOpen = (o: { stage: string }) => STAGES.some((s) => s.id === o.stage);
  const open = all.filter(isOpen);
  const in30 = open.filter((o) => { const d = daysUntil(o.closeDate); return d !== null && d <= 30; });
  const rows = view === "won" ? all.filter((o) => !isOpen(o)) : view === "month" ? in30 : view === "all" ? all : open;
  const total = rows.reduce((t, o) => t + (o.amount ?? 0), 0);
  const weighted = open.reduce((t, o) => t + (o.amount ?? 0) * (o.probability ?? 0) / 100, 0);
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };
  const csv = () => {
    const head = ["No.", "Description", "Account", "Stage", "Estimated Value", "Probability %", "Expected Closing Date", "Primary Quote", "Salesperson"];
    const esc = (v: unknown) => { const t = v === null || v === undefined ? "" : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const body = rows.map((o) => [o.number, o.name, o.accountName, STAGE_NAME[o.stage] ?? o.stage, o.amount ?? "", o.probability ?? "", o.closeDate ?? "", o.primaryQuote?.number ?? "", o.ownerName ?? ""]);
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([[head, ...body].map((r) => r.map(esc).join(",")).join("\r\n")], { type: "text/csv" })); a.download = "opportunities.csv"; a.click();
  };
  return (
    <>
      <CommandBar status={data ? `${rows.length} ${rows.length === 1 ? "opportunity" : "opportunities"}` : ""}>
        <Link className="cmd" to="/opportunities/new">{ICON.plus}New</Link>
        <button className="cmd" aria-pressed={shape === "board"} onClick={() => set("as", shape === "board" ? null : "board")}>{shape === "board" ? ICON.list : ICON.board}{shape === "board" ? "Show as List" : "Show as Board"}</button>
        <button className="cmd" disabled={!rows.length} onClick={csv}>{ICON.excel}Open in Excel</button>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title"><h1>Opportunities</h1></div>
        <ErrorBox error={error} />
        <div className="cues">
          <button className="cue as-btn" onClick={() => set("view", null)}><span className="cue-l">Open Opportunities</span><span className="cue-v">{open.length}</span></button>
          <div className="cue"><span className="cue-l">Estimated Value (USD)</span><span className="cue-v">{usd0(open.reduce((t, o) => t + (o.amount ?? 0), 0))}</span></div>
          <div className="cue"><span className="cue-l">Weighted Value (USD)</span><span className="cue-v">{usd0(weighted)}</span></div>
          <button className="cue as-btn" onClick={() => set("view", "month")}><span className="cue-l">Closing in 30 Days</span><span className="cue-v">{in30.length}</span></button>
          <button className="cue as-btn" onClick={() => set("view", "won")}><span className="cue-l">Won (USD)</span><span className="cue-v">{usd0(all.filter((o) => o.stage === "won").reduce((t, o) => t + (o.amount ?? 0), 0))}</span></button>
        </div>
        <div className="panel">
          <div role="tablist" aria-label="Views" className="views">
            {[["mine", "Open Opportunities"], ["month", "Closing in 30 Days"], ["won", "Won and Lost"], ["all", "All"]].map(([id, name]) => (
              <button key={id} role="tab" aria-selected={view === id} className={view === id ? "on" : ""} onClick={() => set("view", id === "mine" ? null : id)}>{name}</button>
            ))}
          </div>
          {!data ? <div className="empty">Loading…</div> : !rows.length ? (
            <div className="empty">No opportunities in this view. <Link to="/opportunities/new">New opportunity</Link></div>
          ) : shape === "board" ? (
            <div className="board pad-x pad-b">
              {STAGES.map((s) => {
                const col = rows.filter((o) => o.stage === s.id);
                return (
                  <section key={s.id} className="board-col" aria-label={s.name}>
                    <div className="board-h"><span className="nm">{s.name}</span><span className="ct">{col.length}</span><span className="sum">{usd0(col.reduce((t, o) => t + (o.amount ?? 0), 0))}</span></div>
                    {col.map((o) => (
                      <Link key={o.id} to={`/opportunities/${o.number}`} className="deal">
                        <span className="deal-n">{o.name}</span><span className="deal-a">{o.accountName}</span>
                        <span className="deal-row"><span className="deal-amt">{usd0(o.amount ?? 0)}</span><span className="deal-d">{usDate(o.closeDate)}</span></span>
                      </Link>
                    ))}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="table-wrap">
              <table className="dgrid num-grid">
                <thead><tr><th>No.</th><th>Description</th><th>Account</th><th>Stage</th><th className="r">Estimated Value</th><th className="r">Probability %</th><th>Expected Closing Date</th><th>Primary Quote</th><th>Salesperson</th></tr></thead>
                <tbody>{rows.map((o) => {
                  const dl = daysUntil(o.closeDate);
                  return (
                    <tr key={o.id}>
                      <td><Link to={`/opportunities/${o.number}`}>{o.number}</Link></td>
                      <td>{o.name}</td>
                      <td><Link to={`/accounts/${o.accountNumber}`}>{o.accountName}</Link></td>
                      <td>{STAGE_NAME[o.stage] ?? label(o.stage)}</td>
                      <td className="r">{usd(o.amount)}</td>
                      <td className="r">{o.probability ?? ""}</td>
                      <td className={isOpen(o) && dl !== null && dl < 0 ? "bad" : ""}>{usDate(o.closeDate)}{isOpen(o) && dl !== null && dl < 0 ? ", overdue" : ""}</td>
                      <td>{o.primaryQuote ? <><Link to={`/quotes/${o.primaryQuote.number}`}>{o.primaryQuote.number}</Link>, {label(o.primaryQuote.state)}</> : "None"}</td>
                      <td>{o.ownerName ?? ""}</td>
                    </tr>
                  );
                })}</tbody>
                <tfoot><tr><td colSpan={4} style={{ fontWeight: 600 }}>Total, {rows.length} {rows.length === 1 ? "opportunity" : "opportunities"}</td><td className="r" style={{ fontWeight: 600 }}>{usd(total)}</td><td colSpan={4} /></tr></tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------------- New ---------------- */

export function NewOpportunity() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data: accounts } = useLoad(() => api.accounts(), []);
  const [v, setV] = useState({ account: params.get("account") ?? "", name: "", closeDate: "", amount: "", source: "", kind: "new_business", competitor: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { const o = await api.createOpportunity({ ...v, amount: v.amount || undefined, closeDate: v.closeDate || undefined }); nav(`/opportunities/${o.number}`); } catch (err) { setError(err); setBusy(false); }
  };
  return (
    <>
      <CommandBar><Link className="cmd" to="/opportunities">← Opportunities</Link></CommandBar>
      <div className="page" style={{ maxWidth: 760 }}>
        <div className="page-title"><h1 className="serif" style={{ fontSize: 30 }}>New opportunity</h1><span className="sub">Starts in Qualify. Add quotes and the buying committee on the record.</span></div>
        <ErrorBox error={error} />
        <form className="dash-card" onSubmit={submit}>
          <div className="field"><label htmlFor="o-acct">Account</label>
            <select id="o-acct" className="select" required value={v.account} onChange={set("account")}>
              <option value="">Choose an account…</option>
              {(accounts ?? []).map((a) => <option key={a.id} value={a.number}>{a.name} ({label(a.lifecycle)})</option>)}
            </select>
            <span className="hint">Not there? <Link to="/accounts/new">Create the account</Link> first.</span>
          </div>
          <div className="field"><label htmlFor="o-name">Opportunity name</label><input id="o-name" className="input" required placeholder="e.g. Line 4 bracket program" value={v.name} onChange={set("name")} /></div>
          <div className="form-grid">
            <div className="field"><label htmlFor="o-close">Expected close</label><input id="o-close" type="date" className="input" value={v.closeDate} onChange={set("closeDate")} /></div>
            <div className="field"><label htmlFor="o-amt">Estimated amount</label><input id="o-amt" inputMode="decimal" className="input" placeholder="Set by the primary quote later" value={v.amount} onChange={set("amount")} /></div>
            <div className="field"><label htmlFor="o-kind">Type</label><select id="o-kind" className="select" value={v.kind} onChange={set("kind")}><option value="new_business">New business</option><option value="existing_business">Existing business</option></select></div>
            <div className="field"><label htmlFor="o-src">Source</label><input id="o-src" className="input" placeholder="Trade show, referral, web…" value={v.source} onChange={set("source")} /></div>
            <div className="field"><label htmlFor="o-comp">Competitor</label><input id="o-comp" className="input" value={v.competitor} onChange={set("competitor")} /></div>
          </div>
          <button className="btn primary" disabled={busy || !v.account || !v.name.trim()}>Create opportunity</button>
        </form>
      </div>
    </>
  );
}

/* ---------------- Opportunity Card ---------------- */

type MailRow = { id: string; subject: string; fromName: string; to: string[]; at: string; preview: string; webLink: string; direction: "in" | "out" };
type MeetRow = { id: string; subject: string; start: string; webLink: string; joinUrl: string | null; attendees: string[] };

function PrimaryQuote({ o }: { o: OpportunityDetail }) {
  const primary = o.quotes.find((q) => q.isPrimary);
  const { data: q, error } = useLoad(() => (primary ? api.quote(primary.id) : Promise.resolve(null)), [primary?.id, primary?.updatedAt]);
  if (!primary) return null;
  const summary = [`${primary.number}, ${label(primary.state)}`, primary.bcQuoteNumber ? `BC Sales Quote ${primary.bcQuoteNumber}` : "Not issued in BC", `${usd(primary.total)} USD`];
  return (
    <XSection title="Primary Quote" chips={summary} actions={<Link to={`/quotes/${primary.number}`} className="linkish">Open Quote</Link>}
      note={<>{summary.join(". ")}.{primary.validUntil ? ` Valid until ${usDate(primary.validUntil)}.` : ""}</>}>
      <ErrorBox error={error} />
      {q && (
        <>
          <div className="table-wrap">
            <table className="dgrid num-grid">
              <thead><tr><th>No.</th><th>Description</th><th className="r">Quantity</th><th className="r">Unit Price Excl. Tax</th><th className="r">Unit Cost</th><th className="r">Margin %</th><th className="r">Line Amount Excl. Tax</th></tr></thead>
              <tbody>{q.lines.map((l) => {
                const qty = Number(l.quantity), price = Number(l.unitPrice), cost = l.unitCost === null ? null : Number(l.unitCost);
                const m = cost === null || !price ? null : (price - cost) / price;
                return (
                  <tr key={l.id}>
                    <td>{l.itemNumber ?? ""}</td><td>{l.description}</td><td className="r">{qty.toLocaleString("en-US")}</td><td className="r">{usd(price)}</td>
                    <td className="r">{cost === null ? "" : usd(cost)}</td><td className={`r ${m !== null && m < q.marginFloor ? "bad" : ""}`}>{m === null ? "" : (m * 100).toFixed(1)}</td><td className="r">{usd(qty * price)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          {!q.lines.length && <p className="dash-empty" style={{ margin: 0 }}>No lines yet. <Link to={`/quotes/${q.number}`}>Add lines on the quote</Link>.</p>}
          <div className="totals">
            <span>Total Excl. Tax (USD)</span><span style={{ fontWeight: 600 }}>{usd(q.total)}</span>
            <span>Total Cost (USD)</span><span>{usd(q.cost)}</span>
            <span>Margin %</span><span>{q.margin === null ? "" : (q.margin * 100).toFixed(1)} <span className="muted">(minimum {(q.marginFloor * 100).toFixed(1)})</span></span>
          </div>
        </>
      )}
    </XSection>
  );
}

function QuoteList({ o, run }: { o: OpportunityDetail; run: (fn: () => Promise<unknown>) => void }) {
  const nav = useNavigate();
  const visible = o.quotes.filter((q) => q.state !== "superseded");
  const superseded = o.quotes.filter((q) => q.state === "superseded");
  const row = (q: QuoteRow) => (
    <tr key={q.id}>
      <td><Link to={`/quotes/${q.number}`}>{q.number}</Link>{q.isPrimary ? " (Primary)" : ""}</td>
      <td>{q.bcQuoteNumber ?? ""}</td><td>{label(q.state)}</td><td className="r">{q.lineCount}</td>
      <td className="r">{q.margin === null ? "" : (q.margin * 100).toFixed(1)}</td><td className="r">{usd(q.total)}</td><td>{usDate(q.validUntil)}</td>
      <td>{!q.isPrimary && !["rejected", "accepted", "superseded"].includes(q.state) && <button className="linkish" onClick={() => run(() => api.makePrimary(q.id))}>Make Primary</button>}</td>
    </tr>
  );
  const isOpen = STAGES.some((s) => s.id === o.stage);
  return (
    <XSection title="Quotes" chips={[`${visible.length} ${visible.length === 1 ? "quote" : "quotes"}`, superseded.length ? `${superseded.length} superseded` : null]} open={false}
      actions={isOpen && <button className="linkish" onClick={() => run(async () => { const q = await api.createQuote({ opportunity: o.id }); nav(`/quotes/${q.number}`); })}>New Quote</button>}
      note="The primary quote sets the Estimated Value. Closing as won converts it to a Business Central sales order.">
      {o.quotes.length ? (
        <div className="table-wrap">
          <table className="dgrid num-grid">
            <thead><tr><th>No.</th><th>BC Quote No.</th><th>Status</th><th className="r">Lines</th><th className="r">Margin %</th><th className="r">Amount Excl. Tax</th><th>Valid Until</th><th aria-label="Actions" /></tr></thead>
            <tbody>{visible.map(row)}{superseded.map(row)}</tbody>
          </table>
        </div>
      ) : <p className="dash-empty" style={{ margin: 0 }}>No quotes yet. Use New Quote to price the deal.</p>}
      {o.unlinkedQuotes.length > 0 && (
        <div className="relate">
          <span className="muted">Quotes on {o.account.name} that aren't on an opportunity:</span>
          {o.unlinkedQuotes.map((q) => (
            <span key={q.id} className="relate-q"><Link to={`/quotes/${q.number}`}>{q.number}</Link> <span className="muted">{usd(q.total)} USD</span>
              <button className="btn" onClick={() => run(() => api.updateQuote(q.id, { opportunity: o.id }))}>Relate to This Opportunity</button></span>
          ))}
        </div>
      )}
    </XSection>
  );
}

function Activities({ o, outlook, outlookError, reloadOutlook }: { o: OpportunityDetail; outlook: { emails: string[]; mail: MailRow[]; meetings: MeetRow[]; note?: string } | null; outlookError: unknown; reloadOutlook: () => void }) {
  const [all, setAll] = useState(false);
  const code = outlookError instanceof ApiError ? outlookError.body?.code : undefined;
  const names = new Map(o.committee.concat(o.contacts as any).filter((c: any) => c.email).map((c: any) => [String(c.email).toLowerCase(), c.name]));
  const who = (emails: string[]) => emails.map((e) => names.get(e.toLowerCase()) ?? e).join(", ");
  const rows = [
    ...(outlook?.mail ?? []).map((m) => ({ at: m.at, type: m.direction === "in" ? "Email Received" : "Email Sent", text: m.subject, url: m.webLink, contact: m.direction === "in" ? m.fromName : who(m.to), status: "" })),
    ...(outlook?.meetings ?? []).map((m) => ({ at: m.start, type: m.joinUrl ? "Teams Meeting" : "Meeting", text: m.subject, url: m.joinUrl ?? m.webLink, contact: who(m.attendees), status: new Date(m.start).getTime() >= Date.now() ? "Scheduled" : "Held" })),
    ...o.activities.filter((a) => !["task", "email", "meeting"].includes(a.kind) || !a.externalUrl).filter((a) => a.kind !== "task").map((a) => ({ at: a.createdAt, type: label(a.kind), text: a.subject, url: a.externalUrl ?? undefined, contact: a.by ?? "", status: "Logged" })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  // An email from the customer newer than anything we sent is waiting for a reply.
  const lastIn = outlook?.mail.find((m) => m.direction === "in"), lastOut = outlook?.mail.find((m) => m.direction === "out");
  const waiting = lastIn && (!lastOut || lastIn.at > lastOut.at) ? lastIn : null;
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <XSection title="Activities" chips={[`${rows.length} ${rows.length === 1 ? "activity" : "activities"}`, waiting ? "reply waiting" : null]}
      actions={<button className="linkish" onClick={reloadOutlook}>Refresh</button>}
      note={outlook?.emails.length ? <>Email and meetings from your Outlook with {outlook.emails.join(", ")}, plus calls and notes logged here. Only you see your mail.</> : undefined}>
      {!!outlookError && (code === "consent_required" || code === "not_configured"
        ? <div className="notify warn"><span>{code === "not_configured" ? "Outlook isn't connected yet." : "Outlook needs an admin to grant courageMFG access (one-time consent)."}</span><Link to="/settings/integrations">Integrations</Link></div>
        : <ErrorBox error={outlookError} />)}
      {outlook?.note && <p className="dash-empty" style={{ margin: 0 }}>{outlook.note}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="dgrid num-grid">
            <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Contact</th><th>Status</th></tr></thead>
            <tbody>{shown.map((r, i) => (
              <tr key={i}>
                <td>{new Date(r.at).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", ...(r.type.includes("Meeting") ? { hour: "numeric", minute: "2-digit" } : {}) })}</td>
                <td>{r.type}</td><td>{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.text}</a> : r.text}</td><td>{r.contact}</td>
                <td className={waiting && r.url === waiting.webLink ? "bad" : ""}>{waiting && r.url === waiting.webLink ? "Awaiting Reply" : r.status}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {rows.length > 8 && <button className="linkish" style={{ alignSelf: "flex-start" }} onClick={() => setAll(!all)}>{all ? "Show fewer" : `Show all ${rows.length}`}</button>}
    </XSection>
  );
}

export function OpportunityPage() {
  const { id = "" } = useParams();
  const go = useNavigate();
  const { data: o, error, reload } = useLoad(() => api.opportunity(id), [id]);
  const { data: outlook, error: outlookError, reload: reloadOutlook } = useLoad(() => (o ? api.outlook(o.id) : Promise.resolve(null)), [o?.id, o?.committee.length, o?.contacts.length]);
  const { data: bc } = useLoad(() => (o && o.account.customers.length ? api.accountBc(o.account.id) : Promise.resolve(null)), [o?.account.id, o?.account.customers.length]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [panel, setPanel] = useState<"" | "edit" | "win" | "lose" | "meeting" | "committee" | "log">("");
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setActionError(null);
    try { await fn(); setPanel(""); await reload(); } catch (e) { setActionError(e); } finally { setBusy(false); }
  };
  useEffect(() => { if (panel) window.scrollTo({ top: 0, behavior: "smooth" }); }, [panel]);

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (!o) return <div className="page"><div className="empty">Loading…</div></div>;

  const isOpen = STAGES.some((s) => s.id === o.stage);
  const idx = STAGES.findIndex((s) => s.id === o.stage);
  const entered = (stage: string) => [...o.history].reverse().find((h) => h.toState === stage)?.at;
  const daysIn = Math.max(0, Math.round((Date.now() - new Date(entered(o.stage) ?? o.createdAt).getTime()) / 864e5));
  const daysOpen = Math.max(0, Math.round((Date.now() - new Date(o.createdAt).getTime()) / 864e5));
  const dleft = daysUntil(o.closeDate);
  const tasks = o.activities.filter((a) => a.kind === "task");
  const openTasks = tasks.filter((t) => !t.doneAt).sort((a, b) => (a.dueDate ?? "9") < (b.dueDate ?? "9") ? -1 : 1);
  const next = openTasks[0];
  const done = new Set(o.checks.map((c) => c.key));
  const stageChecks = CHECKLIST[o.stage] ?? [];
  const winnable = o.quotes.filter((q) => ["approved", "sent"].includes(q.state));
  const decider = o.committee.find((m) => m.role === "decision_maker") ?? o.committee[0];
  const cust = bc?.customers.find((c): c is Customer360 => "customer" in c);
  const fin = cust && cust.financials.ok ? cust.financials.data : null;
  const openOrders = cust && cust.orders.ok ? cust.orders.data.length : null;
  const lastIn = outlook?.mail.find((m) => m.direction === "in"), lastOut = outlook?.mail.find((m) => m.direction === "out");
  const waiting = lastIn && (!lastOut || lastIn.at > lastOut.at) ? lastIn : null;
  const lastActivity = [...o.activities.map((a) => a.createdAt), ...(outlook?.mail.map((m) => m.at) ?? [])].sort().pop();

  return (
    <>
      <CommandBar status={`Updated ${ago(o.updatedAt)}`}>
        <button className="cmd" onClick={() => setPanel(panel === "edit" ? "" : "edit")}>{ICON.edit}Edit</button>
        {isOpen && <button className="cmd" disabled={busy} onClick={() => run(async () => { const q = await api.createQuote({ opportunity: o.id }); go(`/quotes/${q.number}`); })}>{ICON.plus}New Quote</button>}
        {isOpen && idx < STAGES.length - 1 && <button className="cmd" disabled={busy} onClick={() => run(() => api.moveStage(o.id, STAGES[idx + 1].id))}>{ICON.next}Advance to {STAGES[idx + 1].name}</button>}
        {isOpen && <button className="cmd" onClick={() => setPanel(panel === "win" ? "" : "win")}>{ICON.won}Close as Won</button>}
        {isOpen && <button className="cmd" onClick={() => setPanel(panel === "lose" ? "" : "lose")}>{ICON.lost}Close as Lost</button>}
        {o.stage === "lost" && <button className="cmd" disabled={busy} onClick={() => run(() => api.reopenOpportunity(o.id))}>{ICON.next}Reopen</button>}
        <span className="cmd-sep" aria-hidden="true" />
        {isOpen && <button className="cmd" onClick={() => setPanel(panel === "meeting" ? "" : "meeting")}>{ICON.meet}Schedule Teams Meeting</button>}
        <button className="cmd" onClick={() => setPanel(panel === "log" ? "" : "log")}>{ICON.log}Log Activity</button>
        <button className="cmd" onClick={() => setPanel(panel === "committee" ? "" : "committee")}>{ICON.plus}Add Contact</button>
        <Link className="cmd" to={`/accounts/${o.account.number}`}>Account</Link>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title">
          <Link to="/opportunities" className="crumb">Opportunities</Link>
          <div className="line">
            <h1>{o.number} · {o.name}</h1>
            <span className="meta">Status: {isOpen ? "Open" : STAGE_NAME[o.stage]}</span>
            {isOpen && <span className="meta">Stage: {STAGE_NAME[o.stage]} ({idx + 1} of {STAGES.length}), {daysIn} {daysIn === 1 ? "day" : "days"}</span>}
            {o.stage === "won" && o.bcOrderNumber && <span className="meta">BC Sales Order: {o.bcOrderNumber}</span>}
            {o.contractNumber && <span className="meta">Contract: <Link to={`/contracts/${o.contractNumber}`}>{o.contractNumber}</Link></span>}
            {o.stage === "lost" && o.lostReason && <span className="meta">Reason: {o.lostReason}</span>}
          </div>
          {isOpen && (
            <div className="stage-steps" aria-label="Stages">
              {STAGES.map((st, i) => (
                <button key={st.id} className={`st as-link ${i < idx ? "done" : i === idx ? "cur" : ""}`} disabled={busy || i === idx} onClick={() => run(() => api.moveStage(o.id, st.id))} title={i === idx ? "Current stage" : `Move to ${st.name}`}>
                  {i < idx ? "✓ " : ""}{st.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {fin && (fin.overdueAmount ?? 0) > 0 && (
          <div className="notify warn" role="status">
            <WarnIcon /><span>Customer {cust!.customer.number} {cust!.customer.displayName} has an overdue balance of {usd(fin.overdueAmount)} USD.</span>
            <Link to={`/accounts/${o.account.number}`}>Show overdue invoices</Link>
          </div>
        )}
        {waiting && (
          <div className="notify info" role="status">
            <InfoIcon /><span>{waiting.fromName} emailed on {usDate(waiting.at)} ({waiting.subject}) and hasn't had a reply.</span>
            <a href={waiting.webLink} target="_blank" rel="noopener noreferrer">Open email in Outlook</a>
          </div>
        )}
        {isOpen && next?.dueDate && next.dueDate < today() && (
          <div className="notify warn" role="status"><WarnIcon /><span>Next step overdue since {usDate(next.dueDate)}: {next.subject}.</span></div>
        )}
        {isOpen && dleft !== null && dleft < 0 && (
          <div className="notify warn" role="status"><WarnIcon /><span>The Expected Closing Date passed {-dleft} {dleft === -1 ? "day" : "days"} ago. Update it or close the opportunity.</span><button className="linkish" onClick={() => setPanel("edit")}>Edit</button></div>
        )}

        <ErrorBox error={actionError instanceof ApiError && actionError.body?.code ? null : actionError} />
        {actionError instanceof ApiError && actionError.body?.code && <BcNotice error={actionError} />}

        {panel === "win" && (
          <div className="dash-card panel">
            <div className="dash-card-h">Close as Won</div>
            <div className="dash-cap" style={{ marginBottom: 10 }}>Choose the accepted quote. Then use Create Order on the quote, or Create Contract for a delivery schedule.</div>
            {winnable.length ? (
              <form onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); run(() => api.winOpportunity(o.id, String(f.get("q")))); }}>
                {winnable.map((q) => (
                  <label key={q.id} className="radio-row"><input type="radio" name="q" value={q.id} defaultChecked={q.isPrimary || winnable.length === 1} required />
                    <span>{q.number}</span><span>{usd(q.total)} USD</span><span className="muted">{label(q.state)}{q.bcQuoteNumber ? `, BC Sales Quote ${q.bcQuoteNumber}` : ""}</span></label>
                ))}
                <button className="btn primary" disabled={busy}>Close as Won</button>
              </form>
            ) : <p className="dash-empty" style={{ margin: 0 }}>Approve or send a quote first. An opportunity is won on a quote.</p>}
          </div>
        )}
        {panel === "lose" && (
          <form className="dash-card panel" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); run(() => api.loseOpportunity(o.id, [f.get("r"), f.get("n")].filter(Boolean).join(": "))); }}>
            <div className="dash-card-h" style={{ marginBottom: 10 }}>Close as Lost</div>
            <div className="form-grid">
              <div className="field"><label htmlFor="lr">Reason</label><select id="lr" name="r" className="select" required defaultValue=""><option value="" disabled>Choose…</option>{LOST_REASONS.map((r) => <option key={r}>{r}</option>)}</select></div>
              <div className="field"><label htmlFor="ln">Note</label><input id="ln" name="n" className="input" placeholder="Optional" /></div>
            </div>
            <button className="btn" disabled={busy}>Close as Lost</button> <span className="muted" style={{ fontSize: 12 }}>Open quotes on this opportunity are closed as rejected.</span>
          </form>
        )}
        {panel === "edit" && <EditOpportunity o={o} busy={busy} onSave={(b) => run(() => api.updateOpportunity(o.id, b))} />}
        {panel === "meeting" && <MeetingForm o={o} busy={busy} onSave={(b) => run(() => api.scheduleMeeting(o.id, b))} />}
        {panel === "log" && (
          <form className="dash-card panel" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); run(() => api.addActivity(o.id, { kind: String(f.get("k")), subject: String(f.get("s")), body: String(f.get("b") ?? "") })); }}>
            <div className="dash-card-h" style={{ marginBottom: 10 }}>Log Activity</div>
            <div className="form-grid">
              <div className="field"><label htmlFor="lk">Type</label><select id="lk" name="k" className="select"><option value="call">Call</option><option value="note">Note</option></select></div>
              <div className="field full"><label htmlFor="ls">Description</label><input id="ls" name="s" className="input" required placeholder="e.g. Call with Sam Lee about revision B" /></div>
            </div>
            <div className="field"><label htmlFor="lb">Details</label><textarea id="lb" name="b" className="textarea" /></div>
            <button className="btn primary" disabled={busy}>Save</button>
          </form>
        )}
        {panel === "committee" && (() => {
          const free = o.contacts.filter((ct) => !o.committee.some((m) => m.contactId === ct.id));
          return (
            <div className="dash-card panel">
              <div className="dash-card-h" style={{ marginBottom: 10 }}>Add Contact to the Buying Committee</div>
              {free.length > 0 && (
                <form className="mini-form" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); run(() => api.addCommittee(o.id, String(f.get("c")), String(f.get("r")))); }}>
                  <span className="label">From {o.account.name}</span>
                  <select name="c" className="select" required aria-label="Contact">{free.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}{ct.title ? `, ${ct.title}` : ""}</option>)}</select>
                  <select name="r" className="select" aria-label="Role" defaultValue="influencer">{ROLES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
                  <button className="btn primary" disabled={busy}>Add</button>
                </form>
              )}
              <form className="mini-form" onSubmit={(e) => {
                e.preventDefault(); const f = new FormData(e.currentTarget); const g = (k: string) => String(f.get(k) ?? "").trim() || undefined;
                run(async () => { const ct = await api.addContact(o.account.id, { name: g("name")!, title: g("title"), email: g("email") }); await api.addCommittee(o.id, ct.id, String(f.get("r"))); });
              }}>
                <span className="label">{free.length ? "Or a new contact" : `New contact on ${o.account.name}`}</span>
                <input name="name" className="input" required placeholder="Name" aria-label="Contact name" />
                <input name="title" className="input" placeholder="Job Title" aria-label="Job title" />
                <input name="email" type="email" className="input" placeholder="Email (for Outlook)" aria-label="Email" />
                <select name="r" className="select" aria-label="Role" defaultValue="decision_maker">{ROLES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
                <button className="btn primary" disabled={busy}>Add to Committee</button>
                <span className="hint">Also saved as a contact on the account.</span>
              </form>
            </div>
          );
        })()}

        <div className="card-cols">
          <div className="tabs">
            <XSection title="General">
              <div className="bc-fields">
                <dl>
                  <dt>No.</dt><dd>{o.number}</dd>
                  <dt>Description</dt><dd>{o.name}</dd>
                  <dt>Account</dt><dd><Link to={`/accounts/${o.account.number}`}>{o.account.name}</Link></dd>
                  <dt>BC Customer No.</dt><dd>{o.account.customers.length ? o.account.customers.join(", ") : <span className="muted">None yet. <Link to={`/accounts/${o.account.number}`}>Convert the account</Link> to issue quotes in BC.</span>}</dd>
                  <dt>Primary Contact</dt><dd>{decider?.name ?? ""}</dd>
                  <dt>Salesperson</dt><dd>{o.ownerName ?? ""}</dd>
                  <dt>Type</dt><dd>{label(o.kind)}</dd>
                </dl>
                <dl>
                  <dt>Stage</dt><dd>{STAGE_NAME[o.stage] ?? label(o.stage)}</dd>
                  <dt>Estimated Value</dt><dd>{usd(o.amount)}{o.amount !== null ? " USD" : ""} {o.primaryQuote && <span className="muted">from {o.primaryQuote.number}</span>}</dd>
                  <dt>Probability %</dt><dd>{o.probability ?? ""}</dd>
                  <dt>Expected Closing Date</dt><dd className={isOpen && dleft !== null && dleft < 0 ? "bad" : ""}>{usDate(o.closeDate)}</dd>
                  <dt>Forecast Category</dt><dd>{label(o.forecast)}</dd>
                  <dt>Source</dt><dd>{o.source ?? ""}</dd>
                  <dt>Competitor</dt><dd>{o.competitor ?? ""}</dd>
                  <dt>Next Step</dt><dd>{next ? `${next.subject}${next.dueDate ? ` by ${usDate(next.dueDate)}` : ""}` : ""}</dd>
                </dl>
              </div>
            </XSection>

            <PrimaryQuote o={o} />
            <QuoteList o={o} run={run} />
            <Activities o={o} outlook={outlook as any} outlookError={outlookError} reloadOutlook={() => reloadOutlook()} />

            <XSection title="Next Steps" chips={[openTasks.length ? `${openTasks.length} open` : "none open", next?.dueDate ? `next due ${usDate(next.dueDate)}` : null]} open={openTasks.length > 0}>
              {openTasks.map((t) => <Check key={t.id} done={false} text={t.subject} meta={t.dueDate ? `Due ${usDate(t.dueDate)}` : undefined} disabled={busy} onToggle={() => run(() => api.updateActivity(t.id, { done: true }))} />)}
              {tasks.filter((t) => t.doneAt).slice(0, 3).map((t) => <Check key={t.id} done text={t.subject} meta={t.dueDate ? `Due ${usDate(t.dueDate)}` : undefined} disabled={busy} onToggle={() => run(() => api.updateActivity(t.id, { done: false }))} />)}
              <form className="mini-form" onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); const st = String(f.get("s")).trim(); if (st) run(() => api.addActivity(o.id, { kind: "task", subject: st, dueDate: String(f.get("d") || "") || undefined })); e.currentTarget.reset(); }}>
                <input name="s" className="input" placeholder="Add a next step" aria-label="Next step" />
                <input name="d" type="date" className="input" aria-label="Due date" style={{ maxWidth: 170 }} />
                <button className="btn" disabled={busy}>Add</button>
              </form>
            </XSection>

            <XSection title="Buying Committee" chips={[`${o.committee.length} ${o.committee.length === 1 ? "contact" : "contacts"}`, decider ? `${roleOf(decider.role).name}: ${decider.name}` : null]} open={false}
              actions={<button className="linkish" onClick={() => setPanel("committee")}>Add Contact</button>}>
              {o.committee.length ? (
                <div className="table-wrap">
                  <table className="dgrid num-grid">
                    <thead><tr><th>Name</th><th>Job Title</th><th>Role</th><th>Email</th><th>Phone No.</th><th aria-label="Actions" /></tr></thead>
                    <tbody>{o.committee.map((m) => (
                      <tr key={m.id}><td>{m.name}</td><td>{m.title ?? ""}</td><td>{roleOf(m.role).name}</td><td>{m.email ? <a href={`mailto:${m.email}`}>{m.email}</a> : ""}</td><td>{m.phone ?? ""}</td>
                        <td><button className="linkish" aria-label={`Remove ${m.name}`} onClick={() => run(() => api.removeCommittee(o.id, m.id))}>Remove</button></td></tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <p className="dash-empty" style={{ margin: 0 }}>Add who decides and who influences. Outlook email and meetings follow these people.</p>}
            </XSection>

            {isOpen && (
              <XSection title="Stage Requirements" chips={[`${STAGE_NAME[o.stage]}: ${stageChecks.filter((c) => done.has(c.key)).length} of ${stageChecks.length} complete`]} open={false}>
                {stageChecks.map((c) => <Check key={c.key} done={done.has(c.key)} text={c.text} meta={o.checks.find((x) => x.key === c.key)?.doneBy ?? undefined} disabled={busy} onToggle={() => run(() => api.setCheck(o.id, c.key, !done.has(c.key)))} />)}
                {idx > 0 && (
                  <details className="superseded"><summary>Earlier stages</summary>
                    {STAGES.slice(0, idx).flatMap((st) => CHECKLIST[st.id]).map((c) => <Check key={c.key} done={done.has(c.key)} text={c.text} disabled={busy} onToggle={() => run(() => api.setCheck(o.id, c.key, !done.has(c.key)))} />)}
                  </details>
                )}
              </XSection>
            )}

            <XSection title="Stage History" chips={[`Open ${daysOpen} ${daysOpen === 1 ? "day" : "days"}`, isOpen ? `in ${STAGE_NAME[o.stage]} ${daysIn} ${daysIn === 1 ? "day" : "days"}` : null]} open={false}>
              <table className="dgrid num-grid"><thead><tr><th>Stage</th><th>Entered</th><th>By</th><th>Reason</th></tr></thead>
                <tbody>{o.history.map((h, i) => <tr key={i}><td>{STAGE_NAME[h.toState] ?? label(h.toState)}</td><td>{usDate(h.at)}</td><td>{h.actorName ?? ""}</td><td>{h.reason && h.reason !== "created" ? h.reason : ""}</td></tr>)}</tbody></table>
            </XSection>
          </div>

          <aside className="factboxes" aria-label="FactBoxes">
            <section className="factbox">
              <h2>Customer Statistics</h2>
              {cust && fin ? (
                <dl>
                  <dt>Customer No.</dt><dd>{cust.customer.number}</dd>
                  <dt>Balance (USD)</dt><dd>{usd(fin.balance)}</dd>
                  <dt>Balance Due (USD)</dt><dd className={(fin.overdueAmount ?? 0) > 0 ? "bad" : ""}>{usd(fin.overdueAmount)}</dd>
                  <dt>Sales (USD)</dt><dd>{usd(fin.totalSales)}</dd>
                  <dt>Open Sales Orders</dt><dd>{openOrders ?? ""}</dd>
                </dl>
              ) : <p className="empty-note">{o.account.customers.length ? "Loading from Business Central…" : "Shown once the account is a customer in Business Central."}</p>}
            </section>
            <section className="factbox">
              <h2>Opportunity Statistics</h2>
              <dl>
                <dt>Weighted Value (USD)</dt><dd>{usd(o.amount !== null ? o.amount * (o.probability ?? 0) / 100 : null)}</dd>
                <dt>Days Open</dt><dd>{daysOpen}</dd>
                <dt>Days in Stage</dt><dd>{isOpen ? daysIn : ""}</dd>
                <dt>Quotes</dt><dd>{o.quotes.length}</dd>
                <dt>Last Activity</dt><dd>{usDate(lastActivity)}</dd>
              </dl>
            </section>
            <section className="factbox">
              <h2>Primary Contact</h2>
              {decider ? (
                <dl>
                  <dt>Name</dt><dd>{decider.name}</dd>
                  <dt>Job Title</dt><dd>{decider.title ?? ""}</dd>
                  <dt>Role</dt><dd>{roleOf(decider.role).name}</dd>
                  <dt>Email</dt><dd>{decider.email ? <a href={`mailto:${decider.email}`}>{decider.email}</a> : ""}</dd>
                  <dt>Phone No.</dt><dd>{decider.phone ?? ""}</dd>
                </dl>
              ) : <p className="empty-note">No buying committee yet. <button className="linkish" onClick={() => setPanel("committee")}>Add Contact</button></p>}
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}

function WarnIcon() { return <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l7 13H1z" fill="#BC4B09" /><path d="M8 6v4M8 11.5v1" stroke="#fff" strokeWidth="1.4" /></svg>; }
function InfoIcon() { return <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="#0F6C77" /><path d="M8 7v4.5M8 4.5v1" stroke="#fff" strokeWidth="1.4" /></svg>; }

function EditOpportunity({ o, busy, onSave }: { o: OpportunityDetail; busy: boolean; onSave: (b: Record<string, unknown>) => void }) {
  const [v, setV] = useState({ name: o.name, closeDate: o.closeDate ?? "", probability: String(o.probability ?? ""), forecast: o.forecast ?? "pipeline", kind: o.kind ?? "new_business", source: o.source ?? "", competitor: o.competitor ?? "", amount: o.primaryQuote ? "" : o.amount === null ? "" : String(o.amount) });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <form className="dash-card panel" onSubmit={(e) => { e.preventDefault(); onSave({ ...v, probability: v.probability === "" ? null : Number(v.probability), ...(o.primaryQuote ? { amount: undefined } : { amount: v.amount === "" ? null : v.amount }) }); }}>
      <div className="dash-card-h" style={{ marginBottom: 10 }}>Edit opportunity</div>
      <div className="field"><label htmlFor="e-n">Name</label><input id="e-n" className="input" value={v.name} onChange={set("name")} required /></div>
      <div className="form-grid">
        <div className="field"><label htmlFor="e-c">Close date</label><input id="e-c" type="date" className="input" value={v.closeDate} onChange={set("closeDate")} /></div>
        <div className="field"><label htmlFor="e-p">Probability %</label><input id="e-p" inputMode="numeric" className="input" value={v.probability} onChange={set("probability")} /></div>
        <div className="field"><label htmlFor="e-f">Forecast</label><select id="e-f" className="select" value={v.forecast} onChange={set("forecast")}>{["pipeline", "best_case", "commit", "omitted"].map((f) => <option key={f} value={f}>{label(f)}</option>)}</select></div>
        <div className="field"><label htmlFor="e-k">Type</label><select id="e-k" className="select" value={v.kind} onChange={set("kind")}><option value="new_business">New business</option><option value="existing_business">Existing business</option></select></div>
        <div className="field"><label htmlFor="e-s">Source</label><input id="e-s" className="input" value={v.source} onChange={set("source")} /></div>
        <div className="field"><label htmlFor="e-co">Competitor</label><input id="e-co" className="input" value={v.competitor} onChange={set("competitor")} /></div>
        {!o.primaryQuote && <div className="field"><label htmlFor="e-a">Estimated amount</label><input id="e-a" inputMode="decimal" className="input" value={v.amount} onChange={set("amount")} /></div>}
      </div>
      <button className="btn primary" disabled={busy}>Save</button>
    </form>
  );
}

function MeetingForm({ o, busy, onSave }: { o: OpportunityDetail; busy: boolean; onSave: (b: { subject: string; start: string; end: string; teams: boolean; body?: string }) => void }) {
  const tomorrow = useMemo(() => new Date(Date.now() + 864e5).toISOString().slice(0, 10), []);
  const [v, setV] = useState({ subject: `${o.name}: next steps`, date: tomorrow, time: "10:00", minutes: "30", teams: true, body: "" });
  const people = o.committee.length ? o.committee : o.contacts;
  return (
    <form className="dash-card panel" onSubmit={(e) => { e.preventDefault(); const start = new Date(`${v.date}T${v.time}:00`); const end = new Date(start.getTime() + Number(v.minutes) * 60000); onSave({ subject: v.subject, start: start.toISOString(), end: end.toISOString(), teams: v.teams, body: v.body }); }}>
      <div className="dash-card-h">Schedule a meeting</div>
      <div className="dash-cap" style={{ marginBottom: 10 }}>From your Outlook calendar, inviting {people.filter((p) => p.email).map((p) => p.name).join(", ") || "nobody yet: add contacts with email addresses"}.</div>
      <div className="field"><label htmlFor="m-s">Subject</label><input id="m-s" className="input" required value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} /></div>
      <div className="form-grid">
        <div className="field"><label htmlFor="m-d">Date</label><input id="m-d" type="date" className="input" required value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} /></div>
        <div className="field"><label htmlFor="m-t">Start</label><input id="m-t" type="time" className="input" required value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })} /></div>
        <div className="field"><label htmlFor="m-m">Length</label><select id="m-m" className="select" value={v.minutes} onChange={(e) => setV({ ...v, minutes: e.target.value })}>{["15", "30", "45", "60", "90"].map((m) => <option key={m} value={m}>{m} minutes</option>)}</select></div>
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}><input type="checkbox" checked={v.teams} onChange={(e) => setV({ ...v, teams: e.target.checked })} />Teams meeting</label>
      <div className="field"><label htmlFor="m-b">Agenda</label><textarea id="m-b" className="textarea" value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} /></div>
      <button className="btn primary" disabled={busy || !people.some((p) => p.email)}>Send invite</button>
    </form>
  );
}
